/**
 * Phase 66 Plan 02 (D-14) — RBAC permission catalog.
 *
 * Single source of truth for client-side UI gating. Backend authoritative
 * (every endpoint validates its own scope); these definitions mirror the
 * `role_scopes` claim emitted by Phase 56 + 57 + 63 backend issuers and
 * provide a fallback when the JWT lacks role_scopes.
 *
 * @see PATTERNS.md §Auth + RBAC — `src/auth/Can.tsx`
 * @see CONTEXT.md D-14 — RBAC UI
 */

export const PERMISSIONS = [
  "audit:read",
  "audit:write",
  "circuits:admin",
  "users:admin",
  "machines:write",
  "vpn:admin",
  "files:read",
  "files:write",
  "pii:read",
  // Phase 68 Plan 03 — Power Health admin surface.
  "power_health:read",
  // Phase 69 — admin/operator user CRUD + per-user 2FA management.
  // UI-SPEC §7: super_admin (via spread) + circuit_admin (explicit, own
  // circuit only — backend re-validates scope); admin EXCLUDED by default
  // (CONTEXT.md TBD resolved to "no" — promote per-tenant via role_scopes
  // claim if a super_admin grants it). Viewer/operator never get it.
  "admin_users:manage",
  // Phase 70 — machine clone template snapshot / clone / delete management.
  // UI-SPEC §Component Inventory + CONTEXT D-RBAC-01:
  //   - super_admin: via [...PERMISSIONS] spread
  //   - circuit_admin: explicit (own circuit only — backend re-validates)
  //   - admin / operator / viewer: NOT granted by default
  // Backend final-say on every /admin/machine-clone-templates/* endpoint.
  "machine_clone_templates:manage",
  // Phase 72 — Digital Twin sync / restore / purge management.
  // UI-SPEC §Component Inventory + 72-PATTERNS §21:
  //   - super_admin: via [...PERMISSIONS] spread
  //   - circuit_admin: explicit (own circuit only — backend re-validates)
  //   - admin / operator / viewer: NOT granted by default
  // Backend final-say on every /admin/machines/{cpu_id}/digital-twin/*
  // endpoint (Plan 04 router).
  "digital_twins:manage",
] as const;

export type Permission = (typeof PERMISSIONS)[number];

/**
 * Fallback role → permissions map. Used when JWT lacks `role_scopes`
 * (legacy tokens). Server-issued `role_scopes` always takes precedence
 * — see {@link getEffectivePermissions}.
 *
 * NOTE: parity with backend `require_admin` dep is mandatory. ADMIN_ROLES
 * (admin + super_admin) gates `/admin/*` routes; relaxing this without
 * coordinating backend changes opens privilege-escalation vectors.
 */
export const ROLE_PERMISSIONS: Record<string, Permission[]> = {
  super_admin: [...PERMISSIONS],
  admin: [
    "audit:read",
    "users:admin",
    "machines:write",
    "vpn:admin",
    "files:read",
    "files:write",
    "circuits:admin",
    "power_health:read",
  ],
  // Phase 68 D-OFF-03: circuit_admin sees Power Health for own circuit only
  // (backend filters by JWT circuit_id). UI gating tracks backend.
  // Phase 69 (UI-SPEC §7): circuit_admin also manages admin users in its
  // own circuit (backend enforces tenant scope on every endpoint).
  circuit_admin: [
    "machines:write",
    "files:read",
    "power_health:read",
    "admin_users:manage",
    // Phase 70 — circuit_admin can snapshot + clone templates in its own
    // circuit. Cross-circuit clones rejected by backend with 409
    // cross_circuit_clone_denied.
    "machine_clone_templates:manage",
    // Phase 72 — circuit_admin can sync + restore + purge digital twins
    // in its own circuit. Cross-circuit operations rejected by backend
    // with 404 (no info leak per D-SCOPE-02). Manual restore + sync
    // require digital_twins:manage; backend re-validates per request.
    "digital_twins:manage",
  ],
  viewer: ["audit:read"],
};

/**
 * Compute the effective permission set for a given role + role_scopes pair.
 *
 * Strategy: union of (a) server-issued `role_scopes` array (defense-in-depth
 * — most precise) and (b) fallback `ROLE_PERMISSIONS[role]`. If both are
 * absent the user has zero UI permissions; backend still gets the final say.
 *
 * @param role - JWT `role` claim (super_admin / admin / circuit_admin / viewer)
 * @param role_scopes - JWT `role_scopes` array; nullable for legacy tokens
 */
export function getEffectivePermissions(
  role: string | null | undefined,
  role_scopes: readonly string[] | null | undefined,
): readonly Permission[] {
  const fromScopes = (role_scopes ?? []).filter(
    (scope): scope is Permission => (PERMISSIONS as readonly string[]).includes(scope),
  );
  const fromRole = role && ROLE_PERMISSIONS[role] ? ROLE_PERMISSIONS[role] : [];
  // Union via Set to dedupe.
  const all = new Set<Permission>([...fromScopes, ...fromRole]);
  return [...all];
}
